import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Divider,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Avatar,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchPrescriptionDetail,
  resetPrescriptionDetail,
  downloadPrescription,
} from '../features/prescriptions/prescriptionsSlice';
import {
  LocalHospital,
  FileDownload,
  Person,
  MedicalServices,
  AccessTime,
  LocationOn,
  Phone,
  Healing,
  Science,
  AssignmentInd,
} from '@mui/icons-material';
import { formatIndianDate } from '../utils/dateformatter';

const PrescriptionModal = ({ open, handleClose, prescriptionId }) => {
  const dispatch = useDispatch();
  const { prescriptionDetail, loading } = useSelector(
    (state) => state.prescriptions
  );

  useEffect(() => {
    if (prescriptionId) {
      dispatch(fetchPrescriptionDetail(prescriptionId));
    }
    return () => dispatch(resetPrescriptionDetail());
  }, [dispatch, prescriptionId]);

  const handleDownload = () => {
    dispatch(downloadPrescription(prescriptionId));
  };

  if (loading || !prescriptionDetail) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
        <DialogTitle>Prescription</DialogTitle>
        <DialogContent>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  // Extract Data
  const { prescription_number, consultation, medications, created_at, doctor } =
    prescriptionDetail || {};
  const {
    clinic,
    full_name,
    phone,
    experience,
    specialities,
    qualifications,
    languages,
  } = doctor || {};

  const {
    consultation_number,
    symptoms,
    diagnosis,
    consultation_date,
    follow_up_required,
    follow_up_date,
    referrals,
    test_orders,
    patient,
  } = consultation || {};

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
      <DialogTitle
        sx={{
          textAlign: 'center',
          fontWeight: 'bold',
          fontSize: '1.8rem',
          color: '#007BFF',
        }}
      >
        Prescription Details
      </DialogTitle>
      <DialogContent>
        <Paper
          elevation={3}
          sx={{ padding: 3, borderRadius: 2, backgroundColor: '#f9f9f9' }}
        >
          {/* Doctor & Clinic Info */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: '#007BFF', width: 50, height: 50 }}>
                {doctor?.full_name?.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant='h6'>
                  <MedicalServices sx={{ mr: 1 }} /> Dr. {doctor?.full_name}
                </Typography>
                <Typography variant='body2'>
                  {doctor?.specialities?.map((s) => s.name).join(', ')} |{' '}
                  {doctor?.qualifications?.map((q) => q.name).join(', ')}
                </Typography>
                <Typography variant='body2'>
                  Experience: {doctor?.experience} years |{' '}
                  {doctor?.languages?.map((l) => l.name).join(', ')}
                </Typography>
                <Typography variant='body2'>
                  <Phone sx={{ mr: 1 }} /> {doctor?.phone}
                </Typography>
              </Box>
            </Box>
            {clinic && (
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant='h6'>
                  <LocalHospital sx={{ mr: 1 }} /> {clinic?.clinic_name}
                </Typography>
                <Typography variant='body2'>
                  <LocationOn sx={{ mr: 1 }} /> {clinic?.address}
                </Typography>
                <Typography variant='body2'>
                  <Phone sx={{ mr: 1 }} /> {clinic?.phone_number}
                </Typography>
                <Typography variant='body2'>
                  Services: {clinic?.services_offered} | Hours:{' '}
                  {clinic?.working_hours}
                </Typography>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Patient Info */}
          {patient && (
            <Box sx={{ mb: 2 }}>
              <Typography variant='h6'>
                <Person sx={{ mr: 1 }} /> Patient Details
              </Typography>
              <Typography variant='body2'>
                <strong>Name:</strong> {patient?.name}
              </Typography>
              <Typography variant='body2'>
                <strong>Phone:</strong> {patient?.phone_number}
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Consultation Details */}
          <Box sx={{ mb: 2 }}>
            <Typography variant='h6'>
              <AccessTime sx={{ mr: 1 }} /> Consultation Details
            </Typography>
            <Typography variant='body2'>
              <strong>Consultation Number:</strong> {consultation_number}
            </Typography>
            <Typography variant='body2'>
              <strong>Date:</strong> {formatIndianDate(consultation_date)}
            </Typography>
            <Typography variant='body2'>
              <strong>Symptoms:</strong> {symptoms}
            </Typography>
            <Typography variant='body2'>
              <strong>Diagnosis:</strong> {diagnosis}
            </Typography>
            <Typography
              variant='body2'
              sx={{
                color: follow_up_required ? 'red' : 'green',
                fontWeight: 'bold',
              }}
            >
              Follow-Up Required: {follow_up_required ? 'Yes' : 'No'}
            </Typography>
            {follow_up_required && (
              <Typography variant='body2'>
                Follow-Up Date: {formatIndianDate(follow_up_date)}
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Referrals */}
          {referrals?.length > 0 && (
            <>
              <Typography variant='h6'>
                <AssignmentInd sx={{ mr: 1 }} /> Specialist Referrals
              </Typography>
              {referrals.map((ref, index) => (
                <Typography key={index} variant='body2'>
                  ➤ <strong>{ref.referred_to}</strong>: {ref.reason}
                </Typography>
              ))}
              <Divider sx={{ my: 2 }} />
            </>
          )}

          {/* Test Orders */}
          {test_orders?.length > 0 && (
            <>
              <Typography variant='h6'>
                <Science sx={{ mr: 1 }} /> Lab Tests Ordered
              </Typography>
              {test_orders.map((test, index) => (
                <Typography key={index} variant='body2'>
                  ➤ <strong>{test.test_name}</strong>: {test.instructions}
                </Typography>
              ))}
              <Divider sx={{ my: 2 }} />
            </>
          )}

          {/* Medications */}
          <Typography
            variant='h6'
            sx={{ fontWeight: 'bold', textAlign: 'center', mb: 2 }}
          >
            Medications
          </Typography>
          {medications?.length > 0 ? (
            medications.map((med, index) => (
              <Paper
                key={index}
                sx={{
                  padding: 2,
                  my: 1,
                  borderRadius: 2,
                  backgroundColor: '#F5F5F5',
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant='body2'>
                      <strong>Name:</strong> {med.medication_name}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant='body2'>
                      <strong>Dosage:</strong> {med.dosage}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            ))
          ) : (
            <Typography
              variant='body2'
              sx={{ textAlign: 'center', color: 'gray' }}
            >
              No medications prescribed.
            </Typography>
          )}

          {/* Download Button */}
          <Button
            fullWidth
            variant='contained'
            color='primary'
            startIcon={<FileDownload />}
            onClick={handleDownload}
            sx={{ fontWeight: 'bold', borderRadius: 2, mt: 2 }}
          >
            Download Prescription
          </Button>
        </Paper>
      </DialogContent>
    </Dialog>
  );
};

export default PrescriptionModal;
